import {
  ThemeProvider as MaterialThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import enUsLocale from 'date-fns/locale/en-US';
import deLocale from 'date-fns/locale/es';
import { Box, DriftThemeProvider } from 'drift-ui';
import React, { FC, cloneElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import CloverThemeProvider from 'shamrock-clover-ui/dist/clover/Theme/ThemeProvider';
import { Flex } from '../components/Flex';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import NotificationBar from '../components/NotificationBar';
import OutageDialog from '../components/OutageDialog';
import PermissionsBanner from '../components/PermissionsBanner';
import SplashPage from '../components/SplashPage';
import SubHeaderParent from '../components/SubHeaderParent';
import { RootState } from '../configureStore';
import { HIGHWAY_URL } from '../constants/constants';
import { useGetCarrierPermissionsQuery } from '../features/findLoads/permissionsState';
import { setShowJoyrideButton } from '../features/myLanes/state';
import { SnackbarProvider } from '../hooks/useSnackbar';
import { useGetUserFeatureFlagsQuery } from '../state/featureFlagState.';
import styled, { GlobalCss, MaterialTheme } from '../theme';
import {
  getCurrentUser,
  hasBookItNowOrLoadBoardPermissions,
} from '../utilities/authUtils';
import { fetchOutageData } from '../utilities/contentfulUtils';
import UserPermissionsLevelForm from '../components/UserPermissionsLevelForm';

const LoadboardTable = React.lazy(() => import('../features/findLoads'));
const MyLanes = React.lazy(() => import('../features/myLanes'));

enum PermissionLevel {
  COMPLIANCE = 'Compliance',
  PERMISSION_LEVEL = 'Permission level',
  ACCESS_REQUESTED = 'Access Requested',
}
const WebContainer = styled(Flex)`
  margin-top: -2px;
  height: 100%;
`;

const ScrollableContainer = styled(Flex)`
  overflow: auto !important;
  height: calc(100% - 31px);
  justify-content: space-between;
  flex-direction: column;
  font-size: 14px;
`;

const ContentContainer = styled.div`
  min-width: 750px;
`;

const Container = styled.div`
  padding: 0px 18px 0px 18px;
  min-width: 1084px;
`;

const subHeaderStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: 'white',
  borderBottom: '1px #eeeeee solid',
  boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.22)',
  paddingTop: '19px',
  zIndex: 998,
};

const centerContentStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
};

interface RouterProps {
  header?: React.JSX.Element;
  highwayFlow?: React.JSX.Element;
}

const Router: FC<RouterProps> = ({ header, highwayFlow }) => {
  const { t } = useTranslation();
  const [outageData, setOutageData] = React.useState<any>(null);
  const currentUser = getCurrentUser();
  const dispatch = useDispatch();
  const { isFetching: isFFLoading } = useGetUserFeatureFlagsQuery(
    currentUser?.email!
  );

  const { showHighwayRegistrationFlow } = useSelector(
    (state: RootState) => state.showHighwayRegistrationFlow
  );

  const { freightPermissionsFF } = useSelector(
    (state: RootState) => state.featureFlags.flags
  );

  const permissionsBannerDismissed = localStorage.getItem(
    'permissionsBannerDismissed'
  );

  const MINUTE_MS = 900000; //15 mins
  React.useEffect(() => {
    async function getOutageData() {
      let outageData = await fetchOutageData();
      setOutageData(outageData);
    }
    getOutageData();
    let interval = setInterval(getOutageData, MINUTE_MS);

    return () => clearInterval(interval);
  }, []);

  const { data, isFetching: isLoadingFreightPermissions } =
    useGetCarrierPermissionsQuery(currentUser?.mcleod_carrier_id, {
      skip: !freightPermissionsFF,
    });

  const isAppLoading =
    isFFLoading || (freightPermissionsFF && isLoadingFreightPermissions);

  const carrierHasPermissions =
    !isLoadingFreightPermissions &&
    data &&
    hasBookItNowOrLoadBoardPermissions(data.companyPermissions);

  const userHasPermissions = hasBookItNowOrLoadBoardPermissions(
    currentUser?.permissions?.map((x) => x.name) || []
  );

  const showCarrierCompliance =
    freightPermissionsFF &&
    !isLoadingFreightPermissions &&
    !carrierHasPermissions &&
    !!currentUser?.mcleod_carrier_id;

  const userPermissionLevel =
    freightPermissionsFF &&
    carrierHasPermissions &&
    !userHasPermissions &&
    data.companyAdminEmail !== currentUser?.email;

  if (
    freightPermissionsFF &&
    !isLoadingFreightPermissions &&
    (showCarrierCompliance || userPermissionLevel)
  ) {
    dispatch(setShowJoyrideButton(false));
  }

  const getSplashType = () => {
    if (
      currentUser?.metaData?.ryanTransportationOnboarding
        ?.loadboardAccessRequested &&
      userPermissionLevel
    ) {
      return PermissionLevel.ACCESS_REQUESTED;
    }

    if (showCarrierCompliance) {
      return PermissionLevel.COMPLIANCE;
    }
    if (userPermissionLevel) {
      return PermissionLevel.PERMISSION_LEVEL;
    }
    return null;
  };

  const splashType: PermissionLevel | null = getSplashType();

  const splashContent =
    splashType && currentUser?.mcleod_carrier_id ? (
      <SplashPage
        currentUser={currentUser}
        persmissions={data!}
        type={splashType}
      />
    ) : null;

  const renderHighwayFlow = () => {
    if (!highwayFlow) {
      return null;
    }
    return cloneElement(highwayFlow, {
      highwayUrl: HIGHWAY_URL,
    });
  };

  return (
    <StyledEngineProvider injectFirst>
      <MaterialThemeProvider theme={MaterialTheme}>
        <CloverThemeProvider>
          <DriftThemeProvider>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={t('mui_language') === 'en' ? enUsLocale : deLocale}
            >
              <GlobalCss />
              <WebContainer flexDirection="column">
                {!isAppLoading && (
                  <SubHeaderParent
                    prefix={'/freight'}
                    style={subHeaderStyles}
                    v2
                    header={header}
                    tourDisabled={outageData !== null && outageData?.showInApp}
                  />
                )}
                {isAppLoading && (
                  <Box sx={centerContentStyles}>
                    <LoadingSpinner />
                  </Box>
                )}
                {!isAppLoading && splashContent}
                {!isAppLoading &&
                  !splashType &&
                  showHighwayRegistrationFlow &&
                  renderHighwayFlow()}
                {!isAppLoading &&
                  !splashType &&
                  !showHighwayRegistrationFlow && (
                    <ScrollableContainer>
                      <ContentContainer>
                        <SnackbarProvider>
                          <NotificationBar />
                          {!permissionsBannerDismissed &&
                            freightPermissionsFF && (
                              <PermissionsBanner
                                currentUser={currentUser}
                                companyPermissions={data}
                              />
                            )}
                          <Container>
                            <React.Suspense fallback={<LoadingSpinner />}>
                              <Routes>
                                <Route
                                  path="/freight/findLoads/*"
                                  element={<LoadboardTable />}
                                />
                                <Route
                                  path="/freight/myLanes/*"
                                  element={<MyLanes />}
                                />
                                <Route
                                  path="/freight/*"
                                  element={
                                    <Navigate to="/freight/findLoads" replace />
                                  }
                                />
                              </Routes>
                            </React.Suspense>
                          </Container>
                        </SnackbarProvider>
                      </ContentContainer>
                      <OutageDialog
                        data={outageData}
                        isOpen={outageData !== null && outageData?.showInApp}
                      />
                      <Footer />
                    </ScrollableContainer>
                  )}
                <UserPermissionsLevelForm />
              </WebContainer>
            </LocalizationProvider>
          </DriftThemeProvider>
        </CloverThemeProvider>
      </MaterialThemeProvider>
    </StyledEngineProvider>
  );
};

export default Router;
